/* ========== TREEMAP CHART ==========
 * Two modes:
 *   - Flat: each value becomes a tile, sized by value.
 *   - Hierarchical: when series.hierarchy is present (rows × levels), tiles
 *     are grouped by parent level; each parent gets a tinted color and its
 *     children are nested rectangles inside it.
 * ================================= */

import { COLORS } from './utils';

/** Squarified-ish slice/dice layout. */
function layoutItems(items, bx, by, bw, bh) {
  var rects = [];
  function recurse(items, bx, by, bw, bh) {
    if (items.length === 0) return;
    if (items.length === 1) {
      rects.push({ x: bx, y: by, w: bw, h: bh, item: items[0] });
      return;
    }
    var totalVal = items.reduce(function (a, b) { return a + b.value; }, 0);
    if (totalVal === 0) return;
    var sum = 0, splitIdx = 0;
    for (var i = 0; i < items.length; i++) {
      sum += items[i].value;
      if (sum >= totalVal / 2) { splitIdx = i; break; }
    }
    splitIdx = Math.max(0, Math.min(items.length - 2, splitIdx));
    var left = items.slice(0, splitIdx + 1);
    var right = items.slice(splitIdx + 1);
    var leftTotal = left.reduce(function (a, b) { return a + b.value; }, 0);
    var ratio = leftTotal / totalVal;

    if (bw >= bh) {
      recurse(left,  bx,                 by, bw * ratio,       bh);
      recurse(right, bx + bw * ratio,    by, bw * (1 - ratio), bh);
    } else {
      recurse(left,  bx, by,                 bw, bh * ratio);
      recurse(right, bx, by + bh * ratio,    bw, bh * (1 - ratio));
    }
  }
  recurse(items, bx, by, bw, bh);
  return rects;
}

function drawRect(ctx, r, color, label, padding) {
  ctx.fillStyle = color;
  ctx.fillRect(r.x + padding, r.y + padding, r.w - padding * 2, r.h - padding * 2);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.strokeRect(r.x + padding, r.y + padding, r.w - padding * 2, r.h - padding * 2);

  if (label && r.w > 30 && r.h > 18) {
    ctx.fillStyle = '#fff';
    ctx.font = r.w > 60 && r.h > 30 ? 'bold 11px Arial' : '9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var maxLabelW = r.w - 6;
    var displayLabel = ctx.measureText(label).width > maxLabelW
      ? label.substring(0, Math.floor(maxLabelW / 6)) + '…'
      : label;
    ctx.fillText(displayLabel, r.x + r.w / 2, r.y + r.h / 2);
  }
}

export function drawTreemapChart(ctx, x, y, width, height, chartObj) {
  var series = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  if (!series || series.data.length === 0) return;
  var data = series.data.map(function (v) { return Math.abs(v); });
  var total = data.reduce(function (a, b) { return a + b; }, 0);
  if (total === 0) return;

  var hierarchy = series.hierarchy || chartObj.hierarchy;

  // ── Hierarchical treemap ──────────────────────────────────────────────
  // hierarchy is rows × levels: e.g. [["NA","USA"],["NA","Canada"],["EU","UK"]]
  // Group rows by their topmost (leftmost) level, render parents as outer
  // rectangles, then nest children inside.
  if (hierarchy && hierarchy.length > 0 && hierarchy[0].length > 1) {
    var parents = {};   // parentLabel → { value, children: [{value, label, idx}] }
    var parentOrder = [];
    hierarchy.forEach(function (row, idx) {
      var pLabel = row[0] || '';
      var leafLabel = row[row.length - 1] || pLabel;
      if (!parents[pLabel]) {
        parents[pLabel] = { value: 0, children: [], colorIdx: parentOrder.length };
        parentOrder.push(pLabel);
      }
      parents[pLabel].value += data[idx];
      parents[pLabel].children.push({ value: data[idx], label: leafLabel, idx });
    });

    var parentItems = parentOrder.map(function (label) {
      return { value: parents[label].value, label, parent: parents[label] };
    }).sort(function (a, b) { return b.value - a.value; });

    var parentRects = layoutItems(parentItems, x, y, width, height);
    parentRects.forEach(function (pr) {
      var parent = pr.item.parent;
      var baseColor = COLORS[parent.colorIdx % COLORS.length];

      // Outer parent rectangle (just the border + label band)
      ctx.fillStyle = baseColor;
      ctx.fillRect(pr.x, pr.y, pr.w, pr.h);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.strokeRect(pr.x, pr.y, pr.w, pr.h);

      // Children rectangles
      var children = parent.children.slice().sort(function (a, b) { return b.value - a.value; });
      var childRects = layoutItems(children, pr.x + 2, pr.y + 2, pr.w - 4, pr.h - 4);
      childRects.forEach(function (cr) {
        drawRect(ctx, cr, baseColor, cr.item.label, 1);
      });

      // Parent label at top-left
      if (pr.w > 40 && pr.h > 24) {
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(pr.item.label, pr.x + 4, pr.y + 4);
      }
    });
    return;
  }

  // ── Flat treemap (single-level) ───────────────────────────────────────
  var cats = chartObj.categories || [];
  var pointColors = series.pointColors || [];
  var items = data.map(function (v, i) { return { value: v, index: i }; })
                  .sort(function (a, b) { return b.value - a.value; });
  var rects = layoutItems(items, x, y, width, height);
  rects.forEach(function (r) {
    var color = pointColors[r.item.index] || COLORS[r.item.index % COLORS.length];
    var label = cats[r.item.index] || ('' + (r.item.index + 1));
    drawRect(ctx, r, color, label, 1);
  });
}
